// Bilingual JSONB field helpers (motiflink §1.6).
//
// Content tables store translatable prose as a single JSONB column shaped
// `{ ar: '…', en: '…' }` rather than one row per language. These two functions
// are the only sanctioned way to read and write that shape, so every editor
// surface (M3 email templates, M4 CMS blocks, M6 blog) agrees on what "empty"
// means.
//
// The invariant worth stating: a field with no translation has exactly ONE
// representation — `null`. Without that rule the same absence arrives as
// `null`, `{}`, `{ ar: '' }`, or `{ ar: '', en: '' }` depending on which form
// last touched it, and every consumer has to defend against all four.

import type { Locale } from '@fajrarabi/i18n';

/** A translatable field as stored in JSONB. */
export type BilingualValue = Partial<Record<Locale, string>> | null | undefined;

/**
 * Read one locale out of a bilingual field.
 *
 * Returns `''` for a missing locale — never `undefined` — so the result drops
 * straight into a controlled `<input value>` without React switching the input
 * between controlled and uncontrolled.
 *
 * Deliberately does NOT fall back to the other language: motiflink §1.2 kills
 * cross-language fallback everywhere, and an editor silently shown the English
 * string in the Arabic box would save it as though it were a translation.
 */
export function extractField(value: BilingualValue, locale: Locale): string {
  return value?.[locale] ?? '';
}

/**
 * Write one locale into a bilingual field, immutably.
 *
 * - Blank (or whitespace-only) input removes that locale rather than storing
 *   `''`, so "cleared" and "never filled in" are the same state.
 * - When the last locale is removed the whole field collapses to `null`.
 * - The input object is never mutated, so this is safe in a React setState
 *   updater and in optimistic-update rollbacks.
 */
export function patchField(
  value: BilingualValue,
  locale: Locale,
  next: string,
): Partial<Record<Locale, string>> | null {
  const patched: Partial<Record<Locale, string>> = { ...value };

  if (next.trim() === '') {
    delete patched[locale];
  } else {
    patched[locale] = next;
  }

  return Object.keys(patched).length === 0 ? null : patched;
}

/** True when a bilingual field carries no translation in any language. */
export function isFieldEmpty(value: BilingualValue): boolean {
  if (!value) return true;
  return Object.values(value).every((v) => (v ?? '').trim() === '');
}
